import { format, parseISO } from 'date-fns';
import { vi } from 'date-fns/locale';

/**
 * Parse date string to Date object with multiple format support
 * @param dateString - Date string in various formats
 * @returns Date object or null if invalid
 */
export const parseDateString = (dateString: string): Date | null => {
  if (!dateString) {
    return null;
  }

  try {
    let date: Date;

    // Try different date formats
    if (dateString.includes('T')) {
      // ISO format: "2024-01-15T10:30:00Z"
      date = parseISO(dateString);
    } else if (dateString.includes('-')) {
      // Date format: "2024-01-15 10:30"
      date = new Date(dateString.replace(' ', 'T'));
    } else {
      // Try parsing as is
      date = new Date(dateString);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return null;
    }

    return date;
  } catch (error) {
    return null;
  }
};

/**
 * Format date for display with relative time for recent dates
 * @param dateString - Date string to format
 * @param options - Formatting options
 * @returns Formatted date string
 */
export const formatVietnameseDateTime = (
  dateString: string,
  options: {
    showRelative?: boolean;
    format?: string;
    fallbackText?: string;
  } = {}
): string => {
  const {
    showRelative = true,
    format: formatString = 'dd/MM/yyyy HH:mm',
    fallbackText = 'Chưa có dữ liệu'
  } = options;

  const date = parseDateString(dateString);
  
  if (!date) {
    return fallbackText;
  }

  // Get current date for comparison
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

  // Show relative time for recent dates if enabled
  if (showRelative && diffInHours < 24) {
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      return `${diffInMinutes} phút trước`;
    } else {
      return `${Math.floor(diffInHours)} giờ trước`;
    }
  }

  // Format in Vietnamese locale
  return format(date, formatString, { locale: vi });
};

/**
 * Format date for full display with day of week
 * @param dateString - Date string to format
 * @param fallbackText - Text to show if date is invalid
 * @returns Full formatted date string
 */
export const formatVietnameseFullDateTime = (
  dateString: string,
  fallbackText: string = 'Chưa có dữ liệu'
): string => {
  return formatVietnameseDateTime(dateString, {
    showRelative: false,
    format: 'EEEE, dd/MM/yyyy HH:mm:ss',
    fallbackText
  });
};

/**
 * Format date for short display (date only)
 * @param dateString - Date string to format
 * @param fallbackText - Text to show if date is invalid
 * @returns Short formatted date string
 */
export const formatVietnameseDate = (
  dateString: string,
  fallbackText: string = 'Chưa có dữ liệu'
): string => {
  return formatVietnameseDateTime(dateString, {
    showRelative: false,
    format: 'dd/MM/yyyy',
    fallbackText
  });
};

/**
 * Format time for display (time only)
 * @param dateString - Date string to format
 * @param fallbackText - Text to show if date is invalid
 * @returns Formatted time string
 */
export const formatVietnameseTime = (
  dateString: string,
  fallbackText: string = 'Chưa có dữ liệu'
): string => {
  return formatVietnameseDateTime(dateString, {
    showRelative: false,
    format: 'HH:mm',
    fallbackText
  });
};

/**
 * Check if date is today
 * @param dateString - Date string to check
 * @returns True if date is today
 */
export const isToday = (dateString: string): boolean => {
  const date = parseDateString(dateString);
  if (!date) return false;

  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * Check if date is yesterday
 * @param dateString - Date string to check
 * @returns True if date is yesterday
 */
export const isYesterday = (dateString: string): boolean => {
  const date = parseDateString(dateString);
  if (!date) return false;

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
};

/**
 * Get relative time description in Vietnamese
 * @param dateString - Date string to get relative time for
 * @returns Relative time description
 */
export const getVietnameseRelativeTime = (dateString: string): string => {
  const date = parseDateString(dateString);
  if (!date) return 'Chưa có dữ liệu';

  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 1) {
    return 'Vừa xong';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} phút trước`;
  } else if (diffInHours < 24) {
    return `${diffInHours} giờ trước`;
  } else if (diffInDays < 7) {
    return `${diffInDays} ngày trước`;
  } else {
    return formatVietnameseDate(dateString);
  }
};
